





























import { Component, Prop } from 'vue-property-decorator';
import DxDataSource from 'devextreme/data/data_source';
import { Page } from '@/core/components/controls';
import { Table, TableOptions, getTableCaption } from '@/core/components/table';
import { RelocationsPageArgs, RelocationsMapItem } from './types';
import RelocationsTable from './RelocationsTable.vue';
import container from '@/container';
import { duration } from '@/core/filters/duration';
import {
    CONTRACTS,
    FetchServiceContract,
    WindowsServiceContract,
    EventsServiceContract,
    Events,
} from '@/core/contracts';
import { encodeParams, geoDistance, lookupField } from '@/core/helpers';
import { ContextsState, ProductsState } from '@/store';
import { StructuredResult, Relocation, Vehicle } from '@/dto';
import Query from 'devextreme/data/query';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import { localize } from '../../core/filters';

@Component({
    components: {
        TableOptions,
        RelocationsTable,
    },
})
export default class RelocationsPage extends Page {
    @Prop({ default: () => ({}) }) public args!: RelocationsPageArgs;
    @Inject(CONTRACTS.WindowsService)
    public _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.EventsService)
    public _eventsService?: EventsServiceContract;

    public table: Table | null = null;
    public tableKey: string = 'incoming-fixed-relocations-control';
    public items: Relocation[] = [];

    public get tableName(): string {
        return getTableCaption(this.tableKey);
    }

    public get dataSource() {
        const dataSource = new DxDataSource({
            key: 'id' as keyof Relocation,
            load: this.load,
        });

        return dataSource;
    }

    public mounted() {
        this.table = this.getTable().getDataGridInstance();
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public setTable(table: Table): void {
        this.table = table;
    }

    public showRelocationsMap() {
        const relocations = this.filteredData;
        const relocationsMapItems = this.getRelocationsMapPageItems(
            relocations
        );
        this._windowsService!.open('relocations-map-page', {
            relocationsMapItems,
        });
    }

    public getTable(): RelocationsTable {
        const element = this.$refs['table-control'] as unknown;
        return element as RelocationsTable;
    }

    public setImportantDemandFlag() {
        const table = this.getTable();
        const items = table.getSelectedItems();

        if (items.length === 0) {
            return container
                .get<WindowsServiceContract>(CONTRACTS.WindowsService)
                .showDialog(localize('RecordsNotSelected'));
        }
        const important = items.some(item => !item.important);

        table.setImportantDemandFlag(
            items.filter(item => item.important !== important), // update only necessary rows
            important
        );
    }

    public get filteredData(): Relocation[] {
        const filterExpr = this.table!.getCombinedFilter();
        return filterExpr
            ? Query(this.items).filter(filterExpr).toArray()
            : this.items;
    }

    private getRelocationsMapPageItems(
        relocations: any[]
    ): RelocationsMapItem[] {
        const items = relocations.map(item => ({
            source: item.source,
            destination: item.destination,
            startTime: item.start,
            finishTime: item.finish,
            weight: item.weight,
            resource: item.resource,
        }));

        const relocationsMapItems: RelocationsMapItem[] = [];

        items.forEach(i => {
            const relocation = relocationsMapItems.find(
                x =>
                    x.source?.id === i.source?.id &&
                    x.destination?.id === i.destination?.id &&
                    x.startTime === i.startTime &&
                    x.finishTime === i.finishTime &&
                    x.vehicle === i.resource
            );

            if (relocation === undefined) {
                relocationsMapItems.push({
                    source: i.source,
                    destination: i.destination,
                    startTime: i.startTime,
                    finishTime: i.finishTime,
                    vehicle: i.resource,
                    weight: i.weight,
                });
            } else {
                relocation.weight += i.weight;
            }
        });

        return relocationsMapItems;
    }

    private async load() {
        const currentContextId = ContextsState.currentContextId;
        const currentProduct = ProductsState.current;
        const query = { ...this.args };

        if (currentProduct) {
            query.productAlias = currentProduct.alias;
        }

        const url = `/contexts/${currentContextId}/relocations?${encodeParams(
            query
        )}`;
        this.items = await container
            .get<FetchServiceContract>(CONTRACTS.FetchService)
            .get<StructuredResult<Relocation>>(url)
            .then(this.transformData);

        return this.items ? this.items : [];
    }

    private onProcessingFinished({ contextId }: any) {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.table!.refresh();
        }
    }

    private transformData(data: StructuredResult<Relocation>): Relocation[] {
        if (!(data && data.items)) {
            return [];
        }
        for (let index = 0; index < data.items.length; index++) {
            const item = data.items[index];
            item.id = index;

            lookupField(item, 'product', data.products);
            lookupField(item, 'source', data.sites);
            lookupField(item, 'destination', data.sites);
            lookupField(item, 'owner', data.owners);
            lookupField(item, 'resource', data.vehicles);
            lookupField(item, 'project', data.projects);
            lookupField(item, 'object', data.objects);
            lookupField(item, 'orderNumber', data.strings);
            lookupField(item, 'supplier', data.strings);
            lookupField(item, 'waybillNumber', data.strings);
            lookupField(item, 'consignorName', data.strings);
            lookupField(item, 'supplier', data.strings);
            lookupField(item, 'externalInvoiceNumber', data.strings);
            lookupField(item, 'internalInvoiceNumber', data.strings);
            lookupField(item, 'externalData', data.strings);
            lookupField(item, 'objectCode', data.strings);
            lookupField(item, 'planCode', data.strings);
            lookupField(item, 'packAlias', data.strings);
            lookupField(item.resource, 'type', data.resourceTypes);

            if (item?.currentLocationCommentId) {
                item.currentLocationComment =
                    data.strings[item?.currentLocationCommentId];
            }

            if (item?.operationsCraneId) {
                item.operationsCrane = data.strings[item?.operationsCraneId];
            }

            if (item?.placementId) {
                item.placement = data.strings[item?.placementId];
            }

            if (item.startTransshipmentTime && item.finishTransshipmentTime) {
                const startDate = new Date(item.startTransshipmentTime);
                const finishDate = new Date(item.finishTransshipmentTime);
                item.timeDuration = duration(finishDate, startDate);
            }

            item.weight =
                (item.customWeightFactor ?? item.product.weightFactor) *
                item.quantity;
            item.quantity =
                item.quantity /
                (item.customDisplayDenominator ??
                    item.product.displayDenominator);

            if (
                item.destination.latitude &&
                item.destination.longitude &&
                item.resource.latitude &&
                item.resource.longitude
            ) {
                item.distance = geoDistance(
                    item.destination.latitude,
                    item.destination.longitude,
                    item.resource.latitude,
                    item.resource.longitude
                );
            }
        }
        return data.items;
    }
}
